import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Loader2, Send, AlertCircle } from "lucide-react";
import { interpretSituation } from "@/lib/api";
import { DEMO_PRESETS } from "@/data/situations";
import { useApp } from "@/context/AppContext";

const URGENCY_TONE = {
  critical: { color: "var(--lg-red)", label: "CRITICAL" },
  high: { color: "var(--lg-amber)", label: "HIGH" },
  moderate: { color: "var(--lg-cyan)", label: "MODERATE" },
  info: { color: "var(--lg-lime)", label: "INFO" },
};

export function AIInterpreter({ initialText = "" }) {
  const [text, setText] = useState(initialText);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const { speak } = useApp();

  const submit = async (override) => {
    const q = (override ?? text).trim();
    if (!q) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await interpretSituation(q);
      setResult(r);
      speak(`${r.scenario}. Do this now. ${r.do_now.slice(0, 2).join(". ")}`);
    } catch (e) {
      setError("LIFEGRID could not interpret this — try shorter, plain language.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="interpreter" className="relative py-20 md:py-28">
      <div className="absolute inset-0 tac-grid-fine opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">STEP 04 // INTERPRET</span>
          <span className="h-px w-14 bg-[color:var(--lg-line-strong)]" />
        </div>
        <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight max-w-3xl">
          Tell LIFEGRID <span className="text-cyan-accent">what happened</span>.
        </h2>
        <p className="mt-3 max-w-2xl text-[color:var(--lg-text-2)]">
          Use plain language. LIFEGRID converts confusion into a short, action-first plan.
        </p>

        <div className="mt-8 glass rounded-md p-4 md:p-5">
          <div className="flex items-center gap-2 font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-lime)]">
            <Sparkles size={14} /> CLAUDE SONNET 5 · ACTION GUIDANCE
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="e.g. My friend fell down and isn't responding."
            className="mt-3 w-full bg-transparent text-lg md:text-xl placeholder:text-[color:var(--lg-text-3)] focus:outline-none resize-none"
            data-testid="interpreter-input"
          />
          <div className="flex items-center justify-between mt-2 pt-3 border-t border-white/10">
            <div className="hidden md:flex gap-2">
              {DEMO_PRESETS.slice(0, 4).map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    setText(p.text);
                    submit(p.text);
                  }}
                  className="btn-tactile ghost !py-1.5 !px-2 !text-[10px]"
                  data-testid={`preset-${p.id}`}
                >
                  {p.text.slice(0, 34)}…
                </button>
              ))}
            </div>
            <button
              className="btn-tactile primary"
              onClick={() => submit()}
              disabled={loading || !text.trim()}
              data-testid="interpreter-submit"
            >
              {loading ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
              {loading ? "Analysing" : "Interpret"}
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 flex items-center gap-2 text-[color:var(--lg-amber)]">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6"
            data-testid="interpreter-result"
          >
            <div className="flex flex-wrap items-center gap-3">
              <span
                className="font-mono-tac text-[10px] px-2 py-1 border rounded-sm tracking-[0.28em]"
                style={{
                  color: URGENCY_TONE[result.urgency].color,
                  borderColor: URGENCY_TONE[result.urgency].color,
                }}
              >
                {URGENCY_TONE[result.urgency].label}
              </span>
              <span className="font-display text-2xl md:text-3xl font-bold">{result.scenario}</span>
              <span className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-text-3)]">
                SOURCE · {result.source.toUpperCase()}
              </span>
            </div>
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <ResultBlock title="DO THIS NOW" tone="lime" items={result.do_now} testid="result-do-now" />
              <ResultBlock title="THEN" tone="cyan" items={result.then} testid="result-then" />
              <ResultBlock title="AVOID" tone="red" items={result.avoid} testid="result-avoid" />
              <div
                className="glass rounded-md p-5"
                style={{ borderColor: "rgba(204,255,0,0.35)" }}
                data-testid="result-get-help"
              >
                <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-lime)]">GET HELP</div>
                <p className="mt-2 text-base">{result.get_help}</p>
              </div>
            </div>
            <p className="mt-6 font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-text-3)]">
              LIFEGRID GUIDANCE — NOT A SUBSTITUTE FOR PROFESSIONAL OR EMERGENCY SERVICES.
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
}

function ResultBlock({ title, items, tone, testid }) {
  const color =
    tone === "lime" ? "var(--lg-lime)" : tone === "cyan" ? "var(--lg-cyan)" : "var(--lg-red)";
  return (
    <div className="glass rounded-md p-5" data-testid={testid}>
      <div className="font-mono-tac text-[10px] tracking-[0.28em]" style={{ color }}>
        {title}
      </div>
      <ul className="mt-3 space-y-2">
        {items?.map((s, i) => (
          <li key={i} className="flex gap-3">
            <span
              className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
              style={{ background: color }}
            />
            <span className="text-[color:var(--lg-text)]">{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
